/*
 * analytics.js — GetFit store behaviour + funnel tracking (first-party, non-blocking).
 *
 * Phase 4a captures the full on-site journey as sendBeacon requests to the
 * analytics backend (text/plain => no CORS preflight):
 *   - page view (every page, with page type + product/collection context)
 *   - add-to-cart + initiated-checkout (funnel)
 *   - scroll depth (25/50/75/90/100), time on page
 *   - button/CTA clicks, product search, collection filter usage
 *   - size/colour (variant) selection, cart-drawer opens
 *
 * Config + page context injected by snippets/analytics-loader.liquid into
 * window.GF_ANALYTICS. Runs alongside GA/GTM/Klump — it touches none of them.
 */
(function () {
  'use strict';

  var GF = window.GF_ANALYTICS || {};
  var BASE = String(GF.base || '').replace(/\/+$/, '');
  if (!BASE) return; // not configured
  var PAGE = GF.page || {};

  // ---- identity -----------------------------------------------------------
  function getCookie(name) {
    var m = document.cookie.match('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : null;
  }
  function setCookie(name, val, maxAgeSec) {
    document.cookie = name + '=' + encodeURIComponent(val) + ';path=/;max-age=' + maxAgeSec + ';SameSite=Lax';
  }
  function uuid() {
    return 'xxxxxxxxyxxx4xxxyxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    }) + Date.now().toString(36);
  }
  function getOrCreate(name, maxAgeSec) {
    var v = getCookie(name);
    if (!v) { v = uuid(); setCookie(name, v, maxAgeSec); }
    return v;
  }

  // Reuse Shopify's own visitor/session cookies when present; else first-party.
  var VID = getCookie('_shopify_y') || getOrCreate('gf_vid', 60 * 60 * 24 * 365);
  var SID = getCookie('_shopify_s') || getOrCreate('gf_sid', 60 * 30);
  var CID = GF.customerId != null ? String(GF.customerId) : null;

  function beacon(path, data) {
    try {
      var body = JSON.stringify(Object.assign({ sessionId: SID, visitorId: VID, customerId: CID }, data));
      var url = BASE + path;
      var blob = new Blob([body], { type: 'text/plain' }); // text/plain => no CORS preflight
      if (navigator.sendBeacon) navigator.sendBeacon(url, blob);
      else fetch(url, { method: 'POST', body: body, headers: { 'Content-Type': 'text/plain' }, keepalive: true });
    } catch (e) { /* never block the storefront */ }
  }

  function utm() {
    var u;
    try { u = new URLSearchParams(location.search); } catch (e) { return {}; }
    return {
      source: u.get('utm_source'), medium: u.get('utm_medium'),
      campaign: u.get('utm_campaign'), term: u.get('utm_term'), content: u.get('utm_content')
    };
  }

  // Common context attached to every event beacon.
  function ctx(extra) {
    return Object.assign({ pageType: PAGE.type || null, path: PAGE.path || location.pathname }, extra || {});
  }
  function event(name, extra) { beacon('/api/track/event', ctx(Object.assign({ name: name }, extra || {}))); }

  // ---- page view (every page) --------------------------------------------
  function trackPageView() {
    var p = GF.product || {};
    var col = GF.collection || {};
    var key = 'gf_pvp_' + (PAGE.path || location.pathname);
    try {
      var last = +sessionStorage.getItem(key) || 0;
      if (Date.now() - last < 10000) return; // refresh dedupe
      sessionStorage.setItem(key, String(Date.now()));
    } catch (e) {}
    beacon('/api/track/pageview', ctx({
      title: PAGE.title || document.title || null,
      productId: p.id != null ? String(p.id) : null,
      handle: p.handle || col.handle || null,
      collectionId: col.id != null ? String(col.id) : null,
      referrer: document.referrer || null,
      utm: utm()
    }));
  }

  // ---- scroll depth -------------------------------------------------------
  var maxScroll = 0;
  var scrollFired = {};
  function onScroll() {
    var doc = document.documentElement;
    var full = Math.max(doc.scrollHeight, document.body.scrollHeight);
    var seen = (window.pageYOffset || doc.scrollTop) + window.innerHeight;
    var pct = full > 0 ? Math.min(100, Math.round((seen / full) * 100)) : 0;
    if (pct > maxScroll) maxScroll = pct;
    [25, 50, 75, 90, 100].forEach(function (t) {
      if (pct >= t && !scrollFired[t]) {
        scrollFired[t] = true;
        event('scroll', { value: t });
      }
    });
  }

  // ---- time on page (sent once, on first hide / unload) -------------------
  var started = Date.now();
  var timeSent = false;
  function sendTime() {
    if (timeSent) return;
    timeSent = true;
    var secs = Math.min(3600, Math.round((Date.now() - started) / 1000));
    event('time_on_page', { value: secs, meta: { scroll: maxScroll } });
  }

  // ---- cart snapshot (for checkout, captured before navigation) -----------
  var latestCart = null;
  function refreshCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { latestCart = c; })
      .catch(function () {});
  }

  // ---- add to cart (Dawn PubSub) ------------------------------------------
  function trackAddToCart(evt) {
    try {
      var cd = (evt && evt.cartData) || {};
      var pid = cd.product_id || (cd.items && cd.items[0] && cd.items[0].product_id);
      var vid = (evt && evt.productVariantId) || cd.variant_id || (cd.items && cd.items[0] && cd.items[0].variant_id);
      var qty = cd.quantity || (cd.items && cd.items[0] && cd.items[0].quantity) || null;
      if (pid) {
        beacon('/api/track/cart', {
          productId: String(pid),
          variantId: vid != null ? String(vid) : null,
          quantity: qty != null ? Number(qty) : null,
          eventType: 'add'
        });
      }
    } catch (e) {}
    refreshCart();
  }

  // ---- initiated checkout (cart page + drawer) ----------------------------
  function fireCheckout(source) {
    var c = latestCart;
    if (!c || !c.items || !c.items.length) return;
    var items = c.items.map(function (i) {
      return {
        productId: String(i.product_id),
        variantId: i.variant_id != null ? String(i.variant_id) : null,
        quantity: i.quantity,
        unitPrice: i.final_price != null ? i.final_price / 100 : null,
        title: i.product_title || i.title
      };
    });
    var codes = (c.cart_level_discount_applications || []).map(function (d) { return d.title; }).filter(Boolean);
    if (codes.length) event('discount_applied', { text: codes.join(', ') });
    beacon('/api/track/checkout', {
      cartToken: c.token || null,
      subtotal: c.total_price != null ? c.total_price / 100 : null,
      currency: c.currency || null,
      discountCodes: codes,
      items: items,
      source: source
    });
  }

  // ---- clicks: CTAs / buttons ---------------------------------------------
  function trackClicks(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var el = t.closest('button, input[type="submit"], a.button, .button, [data-analytics]');
    if (!el) return;
    // Ignore the analytics-agnostic checkout buttons (handled by fireCheckout).
    var label = (el.getAttribute('aria-label') || el.textContent || el.value || '').replace(/\s+/g, ' ').trim().slice(0, 80);
    var href = el.getAttribute('href') || null;
    event('click', { text: label || null, meta: { href: href, id: el.id || null } });
  }

  // ---- product search -----------------------------------------------------
  function trackSearch() {
    if (PAGE.type === 'search') {
      var q = null;
      try { q = new URLSearchParams(location.search).get('q'); } catch (e) {}
      if (q) event('search', { text: q.slice(0, 120) });
    }
    // Search form submissions (header / predictive search).
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.matches) return;
      if (form.matches('form[action="/search"], form[action*="/search"]')) {
        var input = form.querySelector('input[name="q"]');
        var val = input && input.value ? input.value.trim() : '';
        if (val) event('search', { text: val.slice(0, 120) });
      }
    }, true);
  }

  // ---- collection filter usage --------------------------------------------
  function trackFilters() {
    if (PAGE.type !== 'collection' && PAGE.type !== 'search') return;
    document.addEventListener('change', function (e) {
      var el = e.target;
      if (!el || !el.name) return;
      if (el.name.indexOf('filter.') === 0 || el.name === 'sort_by') {
        var label = el.name === 'sort_by' ? ('sort:' + el.value) : (el.name.replace('filter.', '') + '=' + (el.value || 'on'));
        event('filter', { text: label.slice(0, 120) });
      }
    }, true);
  }

  // ---- size / colour (variant) selection ----------------------------------
  function trackVariantSelect() {
    if (PAGE.type !== 'product') return;
    var p = GF.product || {};
    document.addEventListener('change', function (e) {
      var el = e.target;
      if (!el || !el.closest) return;
      var wrap = el.closest('variant-selects, variant-radios, .product-form__input, fieldset');
      if (!wrap) return;
      var name = el.getAttribute('name') || el.dataset.optionName || 'option';
      var value = el.value;
      if (el.type === 'radio' && !el.checked) return;
      if (!value) return;
      event('variant_select', {
        productId: p.id != null ? String(p.id) : null,
        text: (name + ':' + value).slice(0, 120),
        meta: { option: name, value: value }
      });
    }, true);
  }

  // ---- cart drawer opens --------------------------------------------------
  function trackCartDrawer() {
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('#cart-icon-bubble, .header__icon--cart, a[href="/cart"]')) {
        event('cart_drawer_open');
      }
    }, true);
    // Also catch programmatic opens (Dawn toggles the "active" class on the drawer).
    var drawer = document.querySelector('cart-drawer, #CartDrawer');
    if (drawer && 'MutationObserver' in window) {
      var wasOpen = false;
      new MutationObserver(function () {
        var open = drawer.classList.contains('active') || drawer.classList.contains('animate');
        if (open && !wasOpen) event('cart_drawer_open');
        wasOpen = open;
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
  }

  // ---- wire up ------------------------------------------------------------
  function init() {
    trackPageView();
    refreshCart();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, trackAddToCart);
    }

    // Capture phase so the beacon fires before the checkout form navigates away.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('#checkout, #CartDrawer-Checkout, [name="checkout"]') : null;
      if (!btn) return;
      var source = btn.id === 'CartDrawer-Checkout' ? 'drawer' : 'cart';
      fireCheckout(source);
    }, true);

    document.addEventListener('click', trackClicks, true);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    trackSearch();
    trackFilters();
    trackVariantSelect();
    trackCartDrawer();

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') sendTime();
    });
    window.addEventListener('pagehide', sendTime);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
